/**
 * Analytics Event Hooks - Unified tracking for GA4/GTM/Plausible
 * Integrates with GTM dataLayer for Consent Mode V2 compliance
 *
 * Centralized GA4 module:
 *   - GA4_MEASUREMENT_ID is the single source of truth for the GA4 tag.
 *   - It falls back to the VITE_GA4_MEASUREMENT_ID env var, otherwise to the
 *     placeholder 'G-XXXXXXXXXX'. REPLACE with real GA4 ID provided by owner.
 *   - When the ID is unset or still the placeholder, initAnalytics() does
 *     nothing and every tracker becomes a NO-OP (events still pushed to
 *     dataLayer for GTM, but no gtag.js script is injected).
 */
import { pushToDataLayer, GA4Events, readSavedConsent, initializeConsentDefaults } from './gtm';

// REPLACE with real GA4 ID provided by owner (e.g. 'G-ABCD123456')
export const GA4_MEASUREMENT_ID: string =
  (import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined) || 'G-XXXXXXXXXX';

const GA4_PLACEHOLDER = 'G-XXXXXXXXXX';

export function isAnalyticsEnabled(): boolean {
  return !!GA4_MEASUREMENT_ID && GA4_MEASUREMENT_ID !== GA4_PLACEHOLDER;
}

let _initialized = false;
/**
 * Inject the GA4 gtag.js script. No-op when the ID is missing/placeholder,
 * when running outside a browser, or when called more than once.
 */
export function initAnalytics(): void {
  if (_initialized) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!isAnalyticsEnabled()) return;
  // Only after an explicit, saved analytics acceptance; consent defaults first.
  if (readSavedConsent()?.analytics !== true) return;

  _initialized = true;
  initializeConsentDefaults();

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) {
    (window as any).dataLayer.push(args);
  }
  (window as any).gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA4_MEASUREMENT_ID, { send_page_view: true });
}

type EventName = 
  | 'cta_book_call_click'
  | 'cta_get_quote_click'
  | 'whatsapp_click'
  | 'phone_click'
  | 'email_click'
  | 'form_submit'
  | 'service_card_click'
  | 'language_switch'
  | 'resource_download'
  | 'cookie_consent_saved'
  | 'pricing_package_select'
  | 'package_select'
  | 'pilot_start_click'
  | 'careers_apply_click'
  | 'book_call_click'
  | 'get_quote_click'
  | 'request_security_docs_click'
  | 'security_docs_form_submit'
  | 'career_application_submitted'
  | 'contact_form_submitted'
  | 'lead_submit'
  | 'quote_estimator_completed'
  | 'primary_cta_click'
  | 'booking_click'
  | 'form_submit_attempt';

interface EventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: any;
}

/**
 * Track analytics event - unified tracking via GTM dataLayer
 * Works with GA4, Plausible, or any GTM-configured analytics
 */
export function trackEvent(eventName: EventName, params?: EventParams): void {
  // Push to GTM dataLayer (primary method)
  pushToDataLayer(eventName, {
    event_category: params?.event_category || 'engagement',
    event_label: params?.event_label,
    value: params?.value,
    ...params,
  });

  // Legacy GA4 direct integration (fallback if gtag exists)
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', eventName, {
      event_category: params?.event_category || 'engagement',
      event_label: params?.event_label,
      value: params?.value,
      ...params,
    });
  }

  // Plausible integration
  if (typeof window !== 'undefined' && (window as any).plausible) {
    (window as any).plausible(eventName, { props: params });
  }

  // Console log in development
  if (import.meta.env.DEV) {
    console.log(`[Analytics] ${eventName}`, params);
  }
}

// Convenience functions for specific events
export const trackBookCallClick = (label?: string) => {
  trackEvent('cta_book_call_click', { event_label: label || 'Header CTA' });
  GA4Events.ctaClick('Book Call', label || 'Header');
};

export const trackGetQuoteClick = (label?: string) => {
  trackEvent('cta_get_quote_click', { event_label: label || 'Quote CTA' });
  GA4Events.ctaClick('Get Quote', label || 'CTA');
};

export const trackWhatsAppClick = () => {
  trackEvent('whatsapp_click', { event_category: 'contact' });
  GA4Events.whatsappClick();
};

export const trackFormSubmit = (formName: string, success: boolean = true) => {
  trackEvent('form_submit', { event_label: formName, event_category: 'conversion' });
  GA4Events.formSubmit(formName, success);
  if (success) {
    GA4Events.generateLead({ form_name: formName });
  }
};

/**
 * Engagement-only signal for the site-wide submit listener: a submit event is
 * not a confirmed lead, so this never fires GA4 generate_lead. Confirmed leads
 * are reported once by trackLeadSubmit after the backend responds.
 */
export const trackFormStart = (formName: string) => {
  trackEvent('form_submit_attempt', { event_label: formName, event_category: 'engagement' });
};

export const trackServiceCardClick = (serviceName: string) => {
  trackEvent('service_card_click', { event_label: serviceName, event_category: 'navigation' });
  GA4Events.serviceView(serviceName, 'service_card');
};

export const trackLanguageSwitch = (newLanguage: string) => {
  trackEvent('language_switch', { event_label: newLanguage, event_category: 'preferences' });
};

export const trackPhoneClick = (phoneNumber: string) => {
  trackEvent('phone_click', { event_label: phoneNumber, event_category: 'contact' });
  GA4Events.phoneClick(phoneNumber);
};

export const trackEmailClick = (emailAddress: string) => {
  trackEvent('email_click', { event_label: emailAddress, event_category: 'contact' });
  GA4Events.emailClick(emailAddress);
};

export const trackFileDownload = (fileName: string, fileType: string) => {
  trackEvent('resource_download', { event_label: fileName, event_category: 'download' });
  GA4Events.fileDownload(fileName, fileType);
};

export const trackPilotRequest = () => {
  trackEvent('pilot_start_click', { event_category: 'conversion' });
  GA4Events.pilotRequest();
};

export const trackQuoteRequest = (serviceName?: string, estimatedValue?: number) => {
  GA4Events.quoteRequest(serviceName, estimatedValue);
};
// ===== Batch 5 conversion event helpers =====

/** Fired after a lead is successfully written by the submit-lead edge function. */
export const trackLeadSubmit = (params: {
  service_interest?: string;
  source_page?: string;
  locale?: string;
  form_name?: string;
}) => {
  trackEvent('lead_submit', {
    event_category: 'conversion',
    event_label: params.form_name || params.service_interest || 'lead',
    ...params,
  });
  GA4Events.generateLead({ form_name: params.form_name || 'submit_lead' });
};

/** Fired when the multi-step Quote Estimator is completed (post-success). */
export const trackQuoteEstimatorCompleted = (params: {
  mode: 'bpo' | 'it';
  source_page?: string;
  locale?: string;
  estimated_low?: number;
  estimated_high?: number;
}) => {
  trackEvent('quote_estimator_completed', {
    event_category: 'conversion',
    event_label: params.mode,
    ...params,
  });
  GA4Events.quoteRequest(`quote_estimator_${params.mode}`, params.estimated_high);
};

/** Fired when a primary CTA (Get Estimate / Get Quote / Start Pilot) is clicked. */
export const trackPrimaryCTAClick = (label: string, location?: string) => {
  trackEvent('primary_cta_click', {
    event_category: 'engagement',
    event_label: label,
    cta_location: location,
  });
  GA4Events.ctaClick(label, location || 'unknown');
};

/** Fired when a "book a call" CTA is clicked anywhere. */
export const trackBookingClick = (location?: string) => {
  trackEvent('booking_click', {
    event_category: 'conversion',
    event_label: location || 'booking_cta',
  });
  GA4Events.ctaClick('Book Call', location || 'booking_cta');
};
